import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { HelpCircle, MessageCircle, ExternalLink, Sparkles, MapPin, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const faqs = [
  {
    q: "What file types can I upload?",
    a: "We support PDF, CSV, Excel (.xls/.xlsx), PNG, JPG, and OFX/QBO files. Bank statements from Citizens Bank, Huntington, and others work great in CSV format."
  },
  {
    q: "How does the OCR extraction work?",
    a: "We use Google Gemini AI to read your documents and extract all transactions automatically. It identifies dates, descriptions, amounts, and assigns CACFP categories."
  },
  {
    q: "What is CACFP and why is it important?",
    a: "CACFP (Child and Adult Care Food Program) is a federal program providing reimbursements for meals at childcare centers. These reimbursements count as taxable income and must be reported on Schedule C."
  },
  {
    q: "How do I use the TurboTax export?",
    a: "Go to Tax Prep Document → click Download CSV. In TurboTax, navigate to Business Income → Schedule C. Use the field values from your report to fill in your income and deductions."
  },
  {
    q: "What documents should I upload first?",
    a: "Start with your bank statements (Citizens Bank CSV works perfectly). Then upload CACFP reports, subsidy 1099s, and payroll reports. The app will extract and categorize all transactions."
  },
  {
    q: "How does the trial work?",
    a: "Your 14-day free trial gives you full access to all features. After the trial, plans start at $99/year (Basic). See our Pricing page for Basic / Premium / Premium Plus options."
  },
  {
    q: "Can I edit extracted transactions?",
    a: "Yes! Go to Transactions, click the edit button on any transaction to change the description, amount, or category. You can also mark transactions as reviewed."
  },
  {
    q: "What are the CACFP expense categories?",
    a: "We support: CACFP Food Expense, Non-Food Expense, Utilities, Use Charge for Space, Maintenance, Co-Payments, Subsidy 1099 Income, Payroll, Insurance, and SBA Loan Payments."
  }
];

export default function HelpCenter() {
  const { user } = useAuth();
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    axios.get(`${API}/tax-credits`, { withCredentials: true })
      .then(res => setCredits(res.data))
      .catch(() => {});
  }, []);

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Help Center</h1>
        <p className="text-slate-500 text-sm mt-1">Everything you need to know about Simple Sign In Tax</p>
      </div>

      {/* Tax Credits Center (Feb 2026) */}
      {credits && (
        <div className="bg-white border-2 border-emerald-200 rounded-2xl p-6 shadow-sm" data-testid="tax-credits-section">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="font-extrabold text-slate-900 text-lg" style={{ fontFamily: "Manrope" }}>Employer Childcare Tax Credits</h2>
              <p className="text-xs text-slate-500 mt-0.5">Federal first, with your state's specific credit (if available).</p>
            </div>
          </div>

          {/* Federal */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-blue-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-bold uppercase tracking-wider text-blue-700">🇺🇸 Federal — All 50 States</p>
                <p className="text-sm font-bold text-slate-900 mt-1" style={{ fontFamily: "Manrope" }}>{credits.federal.name}</p>
                <p className="text-xs text-slate-700 mt-1">{credits.federal.credit_summary}</p>
                <p className="text-xs text-slate-500 mt-1"><strong>Max:</strong> {credits.federal.max_per_year_label}</p>
                <a href={credits.federal.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900 mt-2" data-testid="federal-credit-link">
                  <ExternalLink className="w-3 h-3" /> {credits.federal.form} →
                </a>
              </div>
            </div>
          </div>

          {/* Your State */}
          {credits.state_credit ? (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4" data-testid="state-credit-card">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-purple-700 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs font-bold uppercase tracking-wider text-purple-700">📍 Your State — {credits.user_state}</p>
                  <p className="text-sm font-bold text-slate-900 mt-1" style={{ fontFamily: "Manrope" }}>{credits.state_credit.name}</p>
                  {credits.state_credit.credit_pct && (
                    <p className="text-xs text-slate-700 mt-1">
                      {credits.state_credit.credit_pct}% credit
                      {credits.state_credit.max_per_employee && <> · Up to ${credits.state_credit.max_per_employee}/employee</>}
                      {credits.state_credit.max_per_year && <> · Up to ${credits.state_credit.max_per_year.toLocaleString()}/yr</>}
                    </p>
                  )}
                  {credits.state_credit.deadline && (
                    <p className="text-xs text-slate-500 mt-1"><strong>Deadline:</strong> {credits.state_credit.deadline}</p>
                  )}
                  {credits.state_credit.how_to_apply && (
                    <p className="text-xs text-slate-600 mt-2">{credits.state_credit.how_to_apply}</p>
                  )}
                  <a href={credits.state_credit.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-purple-700 hover:text-purple-900 mt-2">
                    <ExternalLink className="w-3 h-3" /> Learn more →
                  </a>
                </div>
              </div>
            </div>
          ) : credits.user_state ? (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-600" data-testid="state-credit-none">
              {credits.user_state} doesn't currently offer an additional employer childcare credit beyond the federal Form 8882 credit.
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
              Tell us your state in Settings → Profile to see state-specific credits.
            </div>
          )}

          <details className="mt-4">
            <summary className="text-xs font-semibold text-slate-600 cursor-pointer hover:text-slate-900">🌎 Other states with employer childcare credit programs</summary>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {credits.all_states_with_programs.filter(s => s !== credits.user_state).map(s => (
                <span key={s} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">{s}</span>
              ))}
            </div>
          </details>

          <p className="text-[11px] text-slate-400 italic mt-4 leading-relaxed">{credits.disclaimer}</p>
        </div>
      )}

      {/* Contact */}
      <div className="rounded-2xl p-7 text-white" style={{ background: "linear-gradient(135deg, #6D28D9, #2563EB)" }}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-extrabold text-xl mb-2" style={{ fontFamily: "Manrope" }}>Need personalized help?</h2>
            <p className="text-white/80 text-sm mb-4">Our team is here to help you with tax questions and document setup.</p>
            <a href="mailto:admin@todayskidzsimplesignin.com"
              data-testid="contact-support-link"
              className="inline-flex items-center gap-2 bg-white text-[#2563EB] font-semibold px-5 py-2.5 rounded-xl text-sm hover:bg-slate-50 transition-all">
              <MessageCircle className="w-4 h-4" /> Contact Support
            </a>
          </div>
          <HelpCircle className="w-20 h-20 text-white/20" />
        </div>
      </div>

      {/* FAQs */}
      <div>
        <h2 className="font-bold text-slate-900 text-base mb-4" style={{ fontFamily: "Manrope" }}>Frequently Asked Questions</h2>
        <div className="space-y-3" data-testid="faq-section">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <h3 className="font-semibold text-slate-900 text-sm mb-2" style={{ fontFamily: "Manrope" }}>{faq.q}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Document Types Reference */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="document-types-reference">
        <h2 className="font-bold text-slate-900 text-base mb-4" style={{ fontFamily: "Manrope" }}>Supported Document Types & Categories</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-semibold text-slate-700 text-sm mb-3">Upload Types</h3>
            <ul className="space-y-2">
              {["Bank Statements (PDF, CSV, QBO, OFX)", "CACFP Reports (PDF, CSV)", "Subsidy/1099 Reports (PDF, CSV)", "Utility Bills (PDF, PNG, JPG)", "Receipts & Invoices (PDF, PNG, JPG)", "Payroll Reports (PDF, CSV, XLSX)"].map(item => (
                <li key={item} className="flex items-center gap-2 text-slate-600 text-sm">
                  <div className="w-1.5 h-1.5 bg-[#2563EB] rounded-full" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-slate-700 text-sm mb-3">Income Categories</h3>
            <ul className="space-y-2">
              {["CACFP Income", "Subsidy 1099 Income", "Co-Payments (Parent Fees)", "Other Income"].map(item => (
                <li key={item} className="flex items-center gap-2 text-emerald-700 text-sm">
                  <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                  {item}
                </li>
              ))}
              <h3 className="font-semibold text-slate-700 text-sm mt-3 mb-2">Expense Categories</h3>
              {["CACFP Food Expense", "CACFP Non-Food Expense", "CACFP Utilities", "Maintenance Expense", "Payroll Expense", "Insurance", "SBA Loan Payment"].map(item => (
                <li key={item} className="flex items-center gap-2 text-red-700 text-sm">
                  <div className="w-1.5 h-1.5 bg-red-400 rounded-full" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
